import { Restaurant } from '@/types/restaurant';
import React, { useCallback, useMemo, useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import Map, { MapRef, Source, Layer, Marker } from 'react-map-gl/mapbox';
import type { LayerProps } from 'react-map-gl/mapbox';
import type { ViewStateChangeEvent } from 'react-map-gl/mapbox';
import { IconButton, Spinner, Tooltip } from '@chakra-ui/react';
import { FOCUS_VISIBLE_RING } from '@/pages/restaurant/components/actions/ActionButtons';
import { LocateFixed } from 'lucide-react';
import { MapPinLegend } from '@/components/map/MapPinLegend';
import { SELECTED_PIN_LAYER_ID, clusterCountLayer, clusterLayer, selectedPinLayer, unclusteredLayer } from '@/components/map/layers';
import { getRatingIconPath, NOT_SURE_EMOJI_CHAR } from '@/utils/restaurantUtils';
import { getFaceMetrics, greyscaleImage, PIN_GREY } from './pinTint';
import { pinUrl, pinUrls } from './pinAssets';
import { isMemberGestureMove } from './moveGesture';
import { getCardPanOptions, type CameraPadding } from './cardCamera';
import { startPinBounce } from './pinHighlight';
import { LOCATE_BUTTON_INSET_PX, LOCATE_BUTTON_SIZE_PX, getLocateButtonTopPx, getLocateCameraOptions } from './locateMe';
import { mapLocateCopy } from '@/copy/mapLocate';
import { createClusterApi, resolveUnclusterZoom, toClusterMap, type UnclusterResult } from './clusterExpansion';
import type { GeoJSONSource, MapMouseEvent, LngLatBoundsLike } from 'mapbox-gl';
import type { Point } from 'geojson';
import * as turf from '@turf/turf';
import 'mapbox-gl/dist/mapbox-gl.css';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

const MAPBOX_TOKEN = 'pk.eyJ1IjoicGFpcmV5b3Zhcm5pIiwiYSI6ImNsenhzc29zeTA3czAycXBucjk3eDVicngifQ.NMpJNcVqKOnqHrwqghjuOw';
const MAPBOX_STYLE = 'mapbox://styles/paireyovarni/cm7b3i3xd007701s01i8rcqij';

/**
 * Composite a grey / half-blue / face-annotated variant of the no-data base
 * pin onto an offscreen canvas and register it with Mapbox under `key`. Used
 * only for the "Unsure" pin states (no personal TasteMatch; see
 * MapPinLegend.tsx) — the twelve personal-rating pin variants have real
 * designed assets and are loaded directly, never composited.
 *  - (always)            — recolors the yellow base body to grey (greyscaleImage, pinTint.ts)
 *  - `blueRightHalf`     — paints the right half of the pin from this (blue)
 *                          image, giving the grey/blue "listed" split
 *  - `face: true`        — draws the 🤔 "not sure" face over the pin's head
 *
 * The caller registers a grey placeholder under `key` first, so the pin is
 * never missing while this runs; on success the composite REPLACES it
 * (removeImage + addImage, guarded by hasImage: the composite and the
 * placeholder differ in size, which rules out `updateImage`). Resolves once the
 * composite is registered. Rejects (with a step-specific message) if the canvas
 * 2D context is unavailable, pixel access throws (e.g. a tainted canvas), the
 * encoded image fails to load, or registering it throws (e.g. the map was
 * already removed) — the placeholder then simply stays, never the yellow base.
 */
function registerTintedPin(
  map: NonNullable<ReturnType<MapRef['getMap']>>,
  key: string,
  baseImage: HTMLImageElement,
  opts: { blueRightHalf?: HTMLImageElement; face?: boolean } = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const canvas = greyscaleImage(baseImage);
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) {
        reject(new Error('canvas 2D context unavailable'));
        return;
      }

      if (opts.blueRightHalf) {
        // Both base images share the same silhouette, so painting the right
        // half of the blue one over the grey one yields a clean left-grey /
        // right-blue split down the pin's centre line.
        const half = Math.floor(canvas.width / 2);
        ctx.save();
        ctx.beginPath();
        ctx.rect(half, 0, canvas.width - half, canvas.height);
        ctx.clip();
        ctx.drawImage(opts.blueRightHalf, 0, 0);
        ctx.restore();
      }

      if (opts.face) {
        // Sized and centered like the level-face disc on the designed
        // personal pins (measured ratios, see pinTint.ts).
        const face = getFaceMetrics(canvas.width, canvas.height);
        ctx.font = `${Math.round(face.fontSize)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(NOT_SURE_EMOJI_CHAR, face.centerX, face.centerY);
      }

      const composedImage = new window.Image();
      composedImage.onload = () => {
        // An exception thrown inside an onload handler is not caught by the surrounding try, so
        // settle here: otherwise a removed map would leave this promise pending forever.
        try {
          if (map.hasImage(key)) map.removeImage(key);
          map.addImage(key, composedImage);
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      composedImage.onerror = () => reject(new Error('composed pin image failed to load'));
      composedImage.src = canvas.toDataURL('image/png');
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Registers a plain grey teardrop (left grey / right blue when `listed`) as
 * raw RGBA pixels, at the pin PNGs' 400x600 size so the layer's icon sizing
 * is unchanged. Needs no canvas and no image decoding, so it works in exactly
 * the situations where registerTintedPin cannot. Never the yellow pin.
 */
function registerPlaceholderPin(
  map: NonNullable<ReturnType<MapRef['getMap']>>,
  key: string,
  listed: boolean
) {
  if (map.hasImage(key)) return;
  const width = 400;
  const height = 600;
  const data = new Uint8Array(width * height * 4);
  const grey = PIN_GREY;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const inHead = (x - 200) ** 2 + (y - 200) ** 2 <= 195 ** 2;
      // Triangle from the head's widest row down to the tip at (200, 595).
      const halfSpan = ((595 - y) / (595 - 200)) * 190;
      const inTip = y >= 200 && Math.abs(x - 200) <= halfSpan;
      if (!inHead && !inTip) continue;
      const i = (y * width + x) * 4;
      const isBlue = listed && x >= 200;
      data[i] = isBlue ? 8 : grey;
      data[i + 1] = isBlue ? 89 : grey;
      data[i + 2] = isBlue ? 255 : grey;
      data[i + 3] = 255;
    }
  }
  map.addImage(key, { width, height, data });
}

export interface RestaurantMapViewHandle {
  resize: () => void;
  flyTo: (center: { lat: number; lng: number }, zoom?: number) => void;
  /**
   * Short ease so `center` becomes the middle of the view, or of the area left
   * after `padding` (instant under reduced motion). The padding applies to this
   * move only (`retainPadding: false`), so the map's real padding stays zero.
   * Programmatic, so it never counts as a member gesture and never starts a
   * search (see `moveGesture.ts`). Zoom is left alone.
   */
  panTo: (center: { lat: number; lng: number }, padding?: CameraPadding) => void;
  /**
   * Like `panTo` for the selected restaurant, but first zooms in if its pin is
   * currently hidden inside a cluster: one `easeTo` with the zoom at which it
   * becomes its own pin (capped at clusterMaxZoom + 1), the center and the
   * padding. A newer call supersedes an older one still being resolved.
   */
  focusRestaurant: (
    restaurantId: number,
    center: { lat: number; lng: number },
    padding?: CameraPadding
  ) => void;
  /**
   * Eases the map to `center` at neighbourhood zoom (instant under reduced motion), abandoning any
   * pending `focusRestaurant`. Programmatic: never a member gesture, never a search.
   */
  recenterTo: (center: { lat: number; lng: number }) => void;
  /** Stops a camera move in flight and abandons any pending `focusRestaurant` (call when the card closes). */
  cancelMove: () => void;
  /** Moves keyboard focus to the map canvas (e.g. after an overlay that held focus closes). */
  focus: () => void;
  /** Container pixel position of a coordinate (null before the map exists). */
  project: (center: { lat: number; lng: number }) => { x: number; y: number } | null;
  /** The map container's viewport rect (null before the map exists). */
  getContainerRect: () => { top: number; bottom: number; width: number; height: number } | null;
}

export interface RestaurantMapViewProps {
  /** Array of restaurants to display on the map */
  restaurants: Restaurant[];
  /**
   * The restaurant whose card is open. Its pin is drawn enlarged, above the
   * others, with a 2-second intro bounce each time it becomes selected that tapers
   * into a small persistent float for as long as it stays selected. Omit for no highlight.
   */
  selectedRestaurantId?: number | null;
  /**
   * Called for a genuine click/tap on empty map: not after a drag or pinch
   * (Mapbox suppresses `click` once the pointer moved 3px or more), not on a pin
   * or cluster, and not on a DOM overlay inside the map (Pin Key, controls, the
   * user-location marker). Used by the search page to close the selected card.
   */
  onEmptyClick?: () => void;
  /**
   * Shows a round 44px "back to my location" button in the map's top-right corner and
   * calls this when it is pressed. The map component owns the button (it shares the corner
   * with the dev zoom readout), the parent owns what pressing does (reading the member's
   * location, closing the card, the message when location is off). Omit for no button:
   * the previews and the list map do not get one.
   */
  onLocate?: () => void;
  /**
   * The locate press is being worked on (reading the member's location takes seconds). The button
   * shows it is busy (spinner, `aria-busy`) and ignores presses until it clears.
   */
  isLocating?: boolean;
  /**
   * Called when the member themselves starts moving or zooming the map (a gesture, not one of our
   * programmatic moves). The search page uses it to drop a locate result that arrives after it.
   */
  onMemberMove?: () => void;
  /** Callback when a restaurant is selected (clicked) */
  onRestaurantSelect?: (restaurant: Restaurant | null) => void;
  /** User's current location */
  userLocation?: { lat: number; lng: number } | null;
  /** Whether to show the user location marker */
  showUserLocation?: boolean;
  /** Whether the user location marker is draggable */
  draggableUserLocation?: boolean;
  /** Callback when user drags the location marker to a new position */
  onUserLocationDragEnd?: (location: { lat: number; lng: number }) => void;
  /**
   * Callback fired (debounced) when the map camera comes to rest after
   * panning/zooming, so panning the map — not just dragging the user
   * location pin — can trigger a new restaurant search (GitHub #95).
   */
  onMapMoveEnd?: (location: { lat: number; lng: number }) => void;
  /** Initial center point for the map */
  initialCenter?: { lat: number; lng: number };
  /** Initial zoom level */
  initialZoom?: number;
  /** Whether to automatically fit bounds to show all markers */
  fitBoundsOnLoad?: boolean;
  /** Padding for fitBounds */
  fitBoundsPadding?: number;
  /** CSS style for the map container */
  style?: React.CSSProperties;
  /** CSS class name for the map container */
  className?: string;
  /** Whether the map view is currently visible to the user */
  isMapVisible?: boolean;
  /**
   * Show the pin-color key trigger (bottom-left; opens the Pin Key modal). Off by default —
   * intended for the full-screen search map, not small previews like
   * NearbyMapPreview or ViewOnMapModal where there's no room for it.
   */
  showLegend?: boolean;
}

// User location layer configuration
const userLocationLayer = {
  id: 'user-location',
  type: 'symbol',
  source: 'user-location-source',
  layout: {
    'icon-image': 'user-location',
    'icon-size': 1,
    'icon-anchor': 'center',
  }
};

const RESTAURANTS_SOURCE_ID = 'restaurants-source';
/** Layers whose features a click can mean something for (must match `interactiveLayerIds`). */
const INTERACTIVE_LAYER_IDS = [clusterLayer.id, unclusteredLayer.id, selectedPinLayer.id].filter(
  (id): id is string => typeof id === 'string'
);
/** A tap within this many px of a pin or cluster that missed it is not an empty-map tap. */
const EMPTY_TAP_TOLERANCE_PX = 8;
/** How long an empty-map tap waits before closing the card, so a double-tap zoom can cancel it. */
const EMPTY_TAP_CLOSE_DELAY_MS = 300;
/** A focus that has not settled by now is abandoned, so the highlight never waits forever. */
const FOCUS_SAFETY_TIMEOUT_MS = 4000;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/** The GeoJSON feature for a restaurant's pin; shared by the main source and the selected-pin source. */
function toPinFeature(restaurant: Restaurant) {
  return {
    type: 'Feature' as const,
    geometry: {
      type: 'Point' as const,
      coordinates: [restaurant.geometry.location.lng, restaurant.geometry.location.lat],
    },
    properties: {
      id: restaurant.id,
      name: restaurant.name,
      icon: getRatingIconPath(restaurant, false, false, true),
    },
  };
}

export const RestaurantMapView = forwardRef<RestaurantMapViewHandle, RestaurantMapViewProps>(({
  restaurants,
  selectedRestaurantId = null,
  onEmptyClick,
  onLocate,
  isLocating = false,
  onMemberMove,
  onRestaurantSelect,
  userLocation,
  showUserLocation = false,
  draggableUserLocation = false,
  onUserLocationDragEnd,
  onMapMoveEnd,
  initialCenter,
  initialZoom = 12,
  fitBoundsOnLoad = false,
  fitBoundsPadding = 60,
  style = { width: '100%', height: '100%' },
  className,
  isMapVisible = false,
  showLegend = false,
}, ref) => {
  const mapRef = useRef<MapRef>(null);
  const [mapStyleLoaded, setMapStyleLoaded] = useState(false);
  // Track dragged position separately so we can update the marker while dragging
  const [draggedPosition, setDraggedPosition] = useState<{ lat: number; lng: number } | null>(null);
  // Track current zoom level for debug display
  const [currentZoom, setCurrentZoom] = useState(initialZoom);

  // Drag hint tooltip state
  const [hasShownHint, setHasShownHint] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasShownHintRef = useRef(false);

  // Clear any existing timer
  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // Start inactivity timer (3 seconds)
  const startInactivityTimer = useCallback(() => {
    // Use ref to check if already shown (avoids stale closure)
    if (hasShownHintRef.current) return;

    clearInactivityTimer();

    inactivityTimerRef.current = setTimeout(() => {
      setTooltipVisible(true);
      setHasShownHint(true);
      hasShownHintRef.current = true;

      // Auto-dismiss tooltip after 5 seconds
      setTimeout(() => {
        setTooltipVisible(false);
      }, 5000);
    }, 3000);
  }, [clearInactivityTimer]);

  // Start timer when map becomes visible
  useEffect(() => {
    if (isMapVisible && mapStyleLoaded && draggableUserLocation && !hasShownHint) {
      startInactivityTimer();
    }

    return () => {
      clearInactivityTimer();
    };
  }, [isMapVisible, mapStyleLoaded, draggableUserLocation, hasShownHint, startInactivityTimer, clearInactivityTimer]);

  // Map interaction handlers to reset inactivity timer and track analytics
  const handleMapInteraction = useCallback((type: 'pan' | 'zoom') => {
    if (!hasShownHintRef.current) {
      clearInactivityTimer();
      startInactivityTimer();
    }
    analytics.capture(AnalyticsEvents.MAP_INTERACTED, {
      interaction_type: type,
    });
  }, [clearInactivityTimer, startInactivityTimer]);

  // Camera focus bookkeeping for `focusRestaurant`: a token so a newer call (or a
  // cancel) supersedes an older one still resolving; the listeners and timer of the
  // call in flight, so a cancel can remove them; and a pending flag plus waiters so
  // the selected pin's highlight starts only once the camera has arrived.
  const focusTokenRef = useRef(0);
  const focusWorkRef = useRef<() => void>(() => {});
  const focusRef = useRef<{ pending: boolean; waiters: Set<() => void> }>({ pending: false, waiters: new Set() });
  const finishFocus = useCallback(() => {
    focusRef.current.pending = false;
    const waiters = [...focusRef.current.waiters];
    focusRef.current.waiters.clear();
    waiters.forEach((waiter) => waiter());
  }, []);
  /** Abandons the focus in flight (its listeners, timer and result) and releases a waiting highlight. */
  const cancelFocus = useCallback(() => {
    focusTokenRef.current += 1;
    focusWorkRef.current();
    focusWorkRef.current = () => {};
    finishFocus();
  }, [finishFocus]);
  // Nothing may outlive the map: bump the token on unmount (a reused map keeps its listeners).
  useEffect(() => cancelFocus, [cancelFocus]);

  // Expose methods via ref
  // The pending (400ms-debounced) report of a member pan/zoom, see handleMapMoveEnd below.
  // Declared here because recenterTo (in the handle) must be able to cancel it.
  const moveEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useImperativeHandle(ref, () => ({
    resize: () => {
      mapRef.current?.getMap()?.resize();
    },
    flyTo: (center: { lat: number; lng: number }, zoom?: number) => {
      mapRef.current?.flyTo({
        center: [center.lng, center.lat],
        zoom: zoom ?? mapRef.current?.getZoom() ?? 14,
        duration: 1000
      });
    },
    panTo: (center: { lat: number; lng: number }, padding?: CameraPadding) => {
      // A plain pan supersedes any pending focus, and releases a highlight waiting on it.
      cancelFocus();
      mapRef.current?.easeTo(getCardPanOptions(center, padding, prefersReducedMotion()));
    },
    focusRestaurant: (restaurantId: number, center: { lat: number; lng: number }, padding?: CameraPadding) => {
      cancelFocus();
      const token = focusTokenRef.current;
      const mapboxMap = mapRef.current?.getMap();
      if (!mapRef.current || !mapboxMap) return;
      focusRef.current.pending = true;
      const reduced = prefersReducedMotion();
      const live = () => token === focusTokenRef.current;
      // Everything this call attaches, removed together by `cancelFocus` or when it settles.
      const disposers: Array<() => void> = [];
      const stopWork = () => {
        disposers.splice(0).forEach((dispose) => dispose());
      };
      focusWorkRef.current = stopWork;
      const timer = setTimeout(() => {
        // The cluster lookup or the camera never answered: do not leave the highlight waiting.
        if (live()) cancelFocus();
      }, FOCUS_SAFETY_TIMEOUT_MS);
      disposers.push(() => clearTimeout(timer));
      const settle = () => {
        if (!live()) return;
        stopWork();
        finishFocus();
      };
      // One combined camera move; `onDone` runs when THAT move ends. easeTo first stops a
      // move already running and synchronously fires its moveend, so the listener only
      // accepts the moveend that carries this call's token (the interrupted move's does not).
      const move = (zoom: number | undefined, onDone: () => void) => {
        const onMoveEnd = (event: object) => {
          if ((event as { paireFocusToken?: number }).paireFocusToken !== token) return;
          mapboxMap.off('moveend', onMoveEnd);
          onDone();
        };
        mapboxMap.on('moveend', onMoveEnd);
        disposers.push(() => mapboxMap.off('moveend', onMoveEnd));
        mapRef.current?.easeTo(getCardPanOptions(center, padding, reduced, zoom), { paireFocusToken: token });
      };
      const resolve = async (): Promise<UnclusterResult> => {
        const source = mapboxMap.getSource(RESTAURANTS_SOURCE_ID);
        if (!source || source.type !== 'geojson') return { status: 'unclustered' };
        try {
          return await resolveUnclusterZoom({
            map: toClusterMap(mapboxMap),
            sourceId: RESTAURANTS_SOURCE_ID,
            api: createClusterApi(source),
            restaurantId,
            lngLat: [center.lng, center.lat],
            currentZoom: mapboxMap.getZoom(),
          });
        } catch {
          // A source error must not strand the card: just recenter.
          return { status: 'unclustered' };
        }
      };
      void (async () => {
        const first = await resolve();
        if (!live()) return;
        const zoom = first.status === 'zoom' && first.zoom > mapboxMap.getZoom() ? first.zoom : undefined;
        if (first.status !== 'unknown') return move(zoom, settle);
        // The restaurant is off screen, so its cluster is not loaded yet: recenter first, wait
        // for the map to finish loading what is now in view (`idle`, which also fires after a
        // reduced-motion jump), then look again and zoom in if it is hidden.
        move(undefined, () => {
          const onIdle = async () => {
            if (!live()) return;
            const second = await resolve();
            if (!live()) return;
            if (second.status === 'zoom' && second.zoom > mapboxMap.getZoom()) move(second.zoom, settle);
            else settle();
          };
          mapboxMap.once('idle', onIdle);
          disposers.push(() => mapboxMap.off('idle', onIdle));
        });
      })();
    },
    recenterTo: (center: { lat: number; lng: number }) => {
      cancelFocus();
      mapRef.current?.easeTo(getLocateCameraOptions(center, prefersReducedMotion()));
      // Drop a queued pan report: the caller moves the search origin to `center` itself, and a
      // pan that ended just before (or whose glide `easeTo` just stopped, which fires a
      // gesture-tagged moveend synchronously, hence cleared AFTER the ease starts) must not
      // search the old spot after it. Not done in cancelMove: a pan followed by closing the
      // card keeps its search.
      if (moveEndTimerRef.current) clearTimeout(moveEndTimerRef.current);
      moveEndTimerRef.current = null;
    },
    /** Stops a camera move in flight and abandons any pending focus (the card closed). */
    cancelMove: () => {
      cancelFocus();
      mapRef.current?.getMap()?.stop();
    },
    focus: () => {
      mapRef.current?.getCanvas()?.focus({ preventScroll: true });
    },
    project: (center: { lat: number; lng: number }) => {
      const point = mapRef.current?.project([center.lng, center.lat]);
      return point ? { x: point.x, y: point.y } : null;
    },
    getContainerRect: () => {
      const container = mapRef.current?.getContainer();
      if (!container) return null;
      const { top, bottom, width, height } = container.getBoundingClientRect();
      return { top, bottom, width, height };
    }
  }), [cancelFocus, finishFocus]);

  // Determine initial center - use provided, first restaurant, or default
  const center = useMemo(() => {
    if (initialCenter) return initialCenter;
    if (restaurants[0]?.geometry?.location) {
      return {
        lat: restaurants[0].geometry.location.lat,
        lng: restaurants[0].geometry.location.lng,
      };
    }
    // Default to Philadelphia
    return { lat: 39.9526, lng: -75.1652 };
  }, [initialCenter, restaurants]);

  // Create GeoJSON data for restaurants
  const geojsonData = useMemo(() => {
    return {
      type: 'FeatureCollection' as const,
      features: restaurants.map(toPinFeature)
    };
  }, [restaurants]);

  // --- Selected-pin highlight -------------------------------------------
  // `highlightId` is the restaurant whose enlarged pin is showing. It follows
  // `selectedRestaurantId`, but only once the camera has arrived (a card-driven
  // recenter or cluster zoom may still be running), so the bounce is seen where
  // the pin ends up. It changes once per selection; the bounce itself runs on
  // requestAnimationFrame against a paint property, never through React state.
  const [highlightId, setHighlightId] = useState<number | null>(null);
  useEffect(() => {
    setHighlightId(null);
    if (selectedRestaurantId === null) {
      // Card closed: abandon any cluster-focus still resolving or easing toward it.
      cancelFocus();
      return;
    }
    const mapboxMap = mapRef.current?.getMap();
    let cancelled = false;
    const reveal = () => {
      if (!cancelled) setHighlightId(selectedRestaurantId);
    };
    // A focus call is made (by the parent's layout effect) before this effect runs.
    const focusState = focusRef.current;
    if (focusState.pending) {
      focusState.waiters.add(reveal);
      return () => {
        cancelled = true;
        focusState.waiters.delete(reveal);
      };
    }
    if (mapboxMap?.isMoving()) {
      mapboxMap.once('moveend', reveal);
      return () => {
        cancelled = true;
        mapboxMap.off('moveend', reveal);
      };
    }
    reveal();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantId, cancelFocus]);

  // One motion loop per reveal; the previous one is cancelled when the selection changes, the map
  // is hidden (list view keeps the selection, the map is only display: none; coming back to the map
  // starts a fresh intro) or on unmount.
  useEffect(() => {
    if (highlightId === null || !isMapVisible) return;
    const mapboxMap = mapRef.current?.getMap();
    if (!mapboxMap) return;
    return startPinBounce({
      reducedMotion: prefersReducedMotion(),
      apply: (liftPx) => {
        if (mapboxMap.getLayer(SELECTED_PIN_LAYER_ID)) {
          // validate: false skips the style-spec check on this per-frame write.
          mapboxMap.setPaintProperty(SELECTED_PIN_LAYER_ID, 'icon-translate', [0, -liftPx], { validate: false });
        }
      },
    });
  }, [highlightId, isMapVisible]);

  const selectedPinData = useMemo(() => {
    const selected = highlightId === null ? undefined : restaurants.find((r) => r.id === highlightId);
    return {
      type: 'FeatureCollection' as const,
      features: selected?.geometry?.location ? [toPinFeature(selected)] : [],
    };
  }, [highlightId, restaurants]);
  // While highlighted, the normal layer skips this restaurant (the highlight layer draws it, label included).
  const restaurantLayer = useMemo(
    () =>
      (highlightId === null
        ? unclusteredLayer
        : {
            ...unclusteredLayer,
            filter: ['all', unclusteredLayer.filter, ['!=', ['get', 'id'], highlightId]],
          }) as LayerProps,
    [highlightId]
  );

  // Create GeoJSON data for user location
  const userLocationData = useMemo(() => {
    if (!userLocation || (userLocation.lat === 0 && userLocation.lng === 0)) return null;
    return {
      type: 'FeatureCollection' as const,
      features: [{
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [userLocation.lng, userLocation.lat]
        },
        properties: {
          id: 'user-location'
        }
      }]
    };
  }, [userLocation]);

  // Calculate bounds based on restaurants and optionally user location
  const calculateBounds = useCallback((): LngLatBoundsLike | null => {
    const points: [number, number][] = restaurants
      .filter(r => r.geometry?.location?.lat && r.geometry?.location?.lng)
      .map(r => [r.geometry.location.lng, r.geometry.location.lat]);

    if (showUserLocation && userLocation && userLocation.lat !== 0 && userLocation.lng !== 0) {
      points.push([userLocation.lng, userLocation.lat]);
    }

    if (points.length === 0) return null;
    if (points.length === 1) return null; // Can't create bounds from single point

    // Use turf.bbox to calculate bounding box
    const collection = turf.featureCollection(points.map(p => turf.point(p)));
    const bbox = turf.bbox(collection);
    // bbox returns [minLng, minLat, maxLng, maxLat]
    return [[bbox[0], bbox[1]], [bbox[2], bbox[3]]] as LngLatBoundsLike;
  }, [restaurants, showUserLocation, userLocation]);

  // Fit bounds when map loads or showUserLocation changes
  useEffect(() => {
    if (!fitBoundsOnLoad || !mapStyleLoaded || !mapRef.current) return;

    const bounds = calculateBounds();
    if (bounds) {
      mapRef.current.fitBounds(bounds, { padding: fitBoundsPadding, duration: 500 });
    } else if (restaurants.length === 1) {
      // Single restaurant - center on it
      const restaurant = restaurants[0];
      mapRef.current.easeTo({
        center: [restaurant.geometry.location.lng, restaurant.geometry.location.lat],
        zoom: 15,
        duration: 500
      });
    } else if (restaurants.length > 0) {
      // Fallback: center on first restaurant
      const restaurant = restaurants[0];
      mapRef.current.easeTo({
        center: [restaurant.geometry.location.lng, restaurant.geometry.location.lat],
        zoom: initialZoom,
        duration: 500
      });
    }
  }, [fitBoundsOnLoad, mapStyleLoaded, showUserLocation, calculateBounds, restaurants, fitBoundsPadding, initialZoom]);

  // The delayed resize / style-loaded flag started by onMapLoad
  const styleLoadedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (styleLoadedTimerRef.current !== null) clearTimeout(styleLoadedTimerRef.current);
      styleLoadedTimerRef.current = null;
    },
    []
  );
  // Map load handler - loads pin images
  const onMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();

    // Load pin images from the shared glob (pinAssets.ts) - PNG format for iOS compatibility
    for (const [sourcePath, resolvedUrl] of Object.entries(pinUrls)) {
      const image = new window.Image();
      const imageName = sourcePath.split('/').pop()?.split('.')[0];
      if (!imageName) continue;
      // Skip what the map never looks up by name: the plain `00_noData*` pins
      // (since 2026-10-04 only the base art the Unsure pins are composited
      // from, below).
      if (imageName.startsWith('00_noData')) continue;

      image.onload = () => {
        // The personal-rating pin variants are real, designed level-colored
        // assets (src/assets/images/pins-png) — load them as-is. Since
        // 2026-10-04 no-personal-match restaurants use the composited "Unsure"
        // pins below. See the Pin Key (MapPinLegend.tsx) for the full state
        // table.
        if (map && !map.hasImage(imageName)) {
          map.addImage(imageName, image);
        }
      };
      image.onerror = () => {
        console.warn(`Failed to load pin image: ${imageName}`);
      };
      image.src = resolvedUrl;
    }

    // "Unsure" pins: no personal TasteMatch (public rating or not — CD
    // decision 2026-10-04, replacing the old public-level-face grey pins and
    // the yellow no-data pin). Composited at runtime from the existing
    // no-data base art, since no grey+🤔 asset exists:
    //  - `00_notSurePin`:     base body recolored YELLOW -> GREY, 🤔 face.
    //  - `00_notSureListPin`: same grey base with the right half painted from
    //                         the blue `00_noDataListPin`, 🤔 face — the
    //                         grey/blue split the old public+listed pin used.
    const loadImage = (url: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });

    const baseUrl = pinUrl('00_noDataPin');
    const blueUrl = pinUrl('00_noDataListPin');
    if (map) {
      const unsureMap = map;
      const unsureKeys = [
        { key: '00_notSurePin', listed: false },
        { key: '00_notSureListPin', listed: true },
      ];
      // Registered synchronously, before anything async: the Unsure pins are never missing (no
      // missing-image warning, no invisible pin) while the composite is made. Safe to call again
      // (it skips a key that is registered) and never throws, so a removed map cannot break the load.
      const placeholdersFor = (keys: typeof unsureKeys) =>
        keys.forEach(({ key, listed }) => {
          try {
            registerPlaceholderPin(unsureMap, key, listed);
          } catch (error) {
            console.warn(`Could not register the grey placeholder for ${key}`, error);
          }
        });
      placeholdersFor(unsureKeys);

      if (!baseUrl || !blueUrl) {
        console.warn('Unsure pin base art not found in pins-png; keeping the grey placeholder pins');
      } else {
        Promise.all([loadImage(baseUrl), loadImage(blueUrl)])
          .then(([baseImage, blueImage]) =>
            Promise.all(
              unsureKeys.map(({ key, listed }) =>
                registerTintedPin(unsureMap, key, baseImage, {
                  blueRightHalf: listed ? blueImage : undefined,
                  face: true,
                }).catch((error) => {
                  console.warn(`Failed to composite Unsure pin ${key}; keeping the grey placeholder`, error);
                  // If the swap got as far as removing the placeholder, put it back.
                  placeholdersFor(unsureKeys.filter((k) => k.key === key));
                })
              )
            )
          )
          .catch((error) => {
            console.warn('Failed to load the Unsure pin base images; keeping the grey placeholder pins', error);
          });
      }
    }

    // Add pulsing user location dot
    const size = 100;
    const pulsingDot = {
      width: size,
      height: size,
      data: new Uint8Array(size * size * 4),
      context: null as CanvasRenderingContext2D | null,

      onAdd: function () {
        const canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        this.context = canvas.getContext('2d');
      },

      render: function () {
        const duration = 1500;
        const t = (performance.now() % duration) / duration;

        const radius = (size / 2) * 0.3;
        const outerRadius = (size / 2) * 0.7 * t + radius;
        const context = this.context;
        if (!context) return false;

        // Clear canvas
        context.clearRect(0, 0, this.width, this.height);

        // Draw outer pulsing circle
        context.beginPath();
        context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
        context.fillStyle = `rgba(66, 133, 244, ${1 - t})`;
        context.fill();

        // Draw white border circle
        context.beginPath();
        context.arc(this.width / 2, this.height / 2, radius + 2, 0, Math.PI * 2);
        context.fillStyle = 'white';
        context.fill();

        // Draw inner blue circle
        context.beginPath();
        context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
        context.fillStyle = '#4285F4';
        context.fill();

        // Update image data
        this.data = new Uint8Array(context.getImageData(0, 0, this.width, this.height).data.buffer);

        // Request next frame
        map?.triggerRepaint();

        return true;
      }
    };

    if (!map?.hasImage('user-location')) {
      map?.addImage('user-location', pulsingDot as any, { pixelRatio: 2 });
    }

    // Force map resize and mark style as loaded after a short delay. The timer is kept so a
    // newer load supersedes it and unmount cancels it (it must not fire after teardown).
    if (styleLoadedTimerRef.current !== null) clearTimeout(styleLoadedTimerRef.current);
    styleLoadedTimerRef.current = setTimeout(() => {
      styleLoadedTimerRef.current = null;
      map?.resize();
      setMapStyleLoaded(true);
    }, 100);
  }, []);

  // The pending close from an empty-map tap (see `onClick`), cancellable by a double-tap zoom.
  const emptyClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelEmptyClick = useCallback(() => {
    if (emptyClickTimerRef.current !== null) clearTimeout(emptyClickTimerRef.current);
    emptyClickTimerRef.current = null;
  }, []);
  useEffect(() => cancelEmptyClick, [cancelEmptyClick]);

  // Click handler for clusters and restaurants
  const onClick = useCallback((event: MapMouseEvent) => {
    if (!event.features || event.features.length === 0) {
      // Empty map. Only a click on the map canvas itself counts: a click on a DOM
      // overlay inside the map container (legend, marker, controls) bubbles here too.
      const target = event.originalEvent?.target;
      if (!onEmptyClick || !(target instanceof HTMLCanvasElement)) return;
      // A tap that just missed a pin or cluster is not an empty tap: look in a small box.
      const map = mapRef.current;
      const layers = INTERACTIVE_LAYER_IDS.filter((id) => map?.getLayer(id));
      const { x, y } = event.point;
      if (
        layers.length > 0 &&
        (map?.queryRenderedFeatures(
          [
            [x - EMPTY_TAP_TOLERANCE_PX, y - EMPTY_TAP_TOLERANCE_PX],
            [x + EMPTY_TAP_TOLERANCE_PX, y + EMPTY_TAP_TOLERANCE_PX],
          ],
          { layers }
        ).length ?? 0) > 0
      ) {
        return;
      }
      // Close slightly late so a double-tap/double-click zoom can cancel it: the second tap,
      // a `dblclick` or a `zoomstart` all clear the pending close.
      if (emptyClickTimerRef.current !== null) {
        cancelEmptyClick();
        return;
      }
      emptyClickTimerRef.current = setTimeout(() => {
        emptyClickTimerRef.current = null;
        onEmptyClick();
      }, EMPTY_TAP_CLOSE_DELAY_MS);
      return;
    }

    const feature = event.features[0];
    if (!feature.layer || !feature.properties) return;

    switch (feature.layer.id) {
      case 'cluster': {
        const clusterId = feature.properties.cluster_id;
        const mapboxSource = mapRef.current?.getMap().getSource('restaurants-source') as unknown as GeoJSONSource;

        mapboxSource?.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          mapRef.current?.easeTo({
            center: (feature.geometry as Point).coordinates as [number, number],
            zoom: zoom || 14
          });
        });
        break;
      }
      case 'restaurant':
      case 'selected-restaurant': {
        const restaurantId = feature.properties.id;
        const restaurant = restaurants.find(r => r.id === restaurantId);
        if (restaurant) {
          // The one RESTAURANT_PIN_CLICKED owner: only a pin tap reaches this branch, for every
          // map where pins are tappable (Search, the View-on-map modal; the profile's nearby preview is
          // pointerEvents none, its taps happen in that modal). Consumers'
          // onRestaurantSelect must not record it again (arrows, swipe and card taps never do).
          analytics.capture(AnalyticsEvents.RESTAURANT_PIN_CLICKED, {
            restaurant_id: restaurant.id,
            restaurant_name: restaurant.name,
            source: 'map',
          });
          if (onRestaurantSelect) {
            onRestaurantSelect(restaurant);
          }
        }
        break;
      }
    }
  }, [restaurants, onRestaurantSelect, onEmptyClick, cancelEmptyClick]);

  // Handle drag events for user location marker
  const onMarkerDrag = useCallback((event: { lngLat: { lng: number; lat: number } }) => {
    setDraggedPosition({ lat: event.lngLat.lat, lng: event.lngLat.lng });
  }, []);

  const onMarkerDragEnd = useCallback((event: { lngLat: { lng: number; lat: number } }) => {
    const newLocation = { lat: event.lngLat.lat, lng: event.lngLat.lng };
    setDraggedPosition(null);
    onUserLocationDragEnd?.(newLocation);
  }, [onUserLocationDragEnd]);

  // Debounce camera-idle notifications so ordinary panning/zooming re-queries
  // restaurants once the gesture settles, rather than on every intermediate
  // moveend fired mid-gesture (GitHub #95).
  //
  // `moveend` also fires for camera changes that are NOT a member gesture:
  // map.resize() (called after switching from list to map view), window
  // resize/orientation change/fullscreen toggle, and programmatic
  // flyTo/fitBounds (city search, recenter-after-pin-drag). Mapbox tags a
  // genuine user-input move with a real `originalEvent`, tags a
  // resize-driven move with an `originalEvent` of type 'resize',
  // 'orientationchange', 'fullscreenchange', or 'webkitfullscreenchange'
  // (see `isMemberGestureMove` in moveGesture.ts), and gives programmatic moves no
  // `originalEvent` at all — the same test mapbox-gl's own GeolocateControl
  // uses internally. Only a real, non-resize originalEvent means the member
  // actually panned/zoomed/dragged the map.
  const handleMapMoveEnd = useCallback((event: ViewStateChangeEvent) => {
    if (!onMapMoveEnd) return;
    const originalEvent = (event as { originalEvent?: { type?: string } }).originalEvent;
    if (!isMemberGestureMove(originalEvent)) return;
    if (moveEndTimerRef.current) clearTimeout(moveEndTimerRef.current);
    const { latitude, longitude } = event.viewState;
    moveEndTimerRef.current = setTimeout(() => {
      onMapMoveEnd({ lat: latitude, lng: longitude });
    }, 400);
  }, [onMapMoveEnd]);

  useEffect(() => {
    return () => {
      if (moveEndTimerRef.current) clearTimeout(moveEndTimerRef.current);
    };
  }, []);

  // Get the current display position for the user location marker
  const displayUserLocation = draggedPosition || userLocation;

  return (
    <Map
      ref={mapRef}
      onLoad={onMapLoad}
      onClick={onClick}
      // Only the member's own gestures count as interaction: programmatic moves
      // (a card-driven recenter or cluster zoom) carry no originalEvent.
      onMoveStart={(e) => {
        if (!isMemberGestureMove((e as { originalEvent?: { type?: string } }).originalEvent)) return;
        // The member took the map: a focus still pending must not zoom it later.
        cancelFocus();
        onMemberMove?.();
        handleMapInteraction('pan');
      }}
      onZoomStart={(e) => {
        cancelEmptyClick(); // a double-tap zoom is not an empty tap
        if (!isMemberGestureMove((e as { originalEvent?: { type?: string } }).originalEvent)) return;
        cancelFocus();
        onMemberMove?.();
        handleMapInteraction('zoom');
      }}
      onDblClick={cancelEmptyClick}
      onZoomEnd={(e) => setCurrentZoom(e.viewState.zoom)}
      onMoveEnd={handleMapMoveEnd}
      interactiveLayerIds={INTERACTIVE_LAYER_IDS}
      reuseMaps={true}
      initialViewState={{
        latitude: center.lat,
        longitude: center.lng,
        zoom: initialZoom
      }}
      mapStyle={MAPBOX_STYLE}
      mapboxAccessToken={MAPBOX_TOKEN}
      style={style}
      // @ts-expect-error TODO: Fix typing: `className` is not in react-map-gl's props (the directive that
      // used to sit on `interactiveLayerIds` was masking this one).
      className={className}
    >
      {mapStyleLoaded && (
        <>
          <Source
            id="restaurants-source"
            type="geojson"
            data={geojsonData}
            cluster={true}
            clusterRadius={50}
            clusterMaxZoom={16}
            promoteId="id"
          >
            <Layer {...clusterLayer} />
            <Layer {...clusterCountLayer} />
            <Layer {...restaurantLayer} />
          </Source>
          {/* The selected restaurant's enlarged, bouncing pin, above every other pin. */}
          <Source id="selected-restaurant-source" type="geojson" data={selectedPinData}>
            <Layer {...selectedPinLayer} />
          </Source>

          {/* User location - draggable marker or static symbol layer */}
          {showUserLocation && displayUserLocation && displayUserLocation.lat !== 0 && displayUserLocation.lng !== 0 && (
            draggableUserLocation ? (
              <Marker
                longitude={displayUserLocation.lng}
                latitude={displayUserLocation.lat}
                draggable={true}
                onDrag={onMarkerDrag}
                onDragEnd={onMarkerDragEnd}
                anchor="center"
              >
                <Tooltip
                  label="Drag to search a different area"
                  isOpen={tooltipVisible}
                  placement="top"
                  hasArrow
                  bg="gray.800"
                  color="white"
                  px={3}
                  py={2}
                  borderRadius="var(--r-chip)"
                  fontSize="sm"
                  fontWeight="medium"
                >
                  <div
                    style={{
                      position: 'relative',
                      width: 80,
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {/* First pulsing ring */}
                    <div
                      style={{
                        position: 'absolute',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-map-blue-glow-50)',
                        animation: 'pulse-ring 2s ease-out infinite',
                      }}
                    />
                    {/* Second pulsing ring (delayed) */}
                    <div
                      style={{
                        position: 'absolute',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-map-blue-glow-50)',
                        animation: 'pulse-ring 2s ease-out infinite 1s',
                      }}
                    />
                    {/* Inner blue dot with white border */}
                    <div
                      style={{
                        position: 'relative',
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-map-blue)',
                        border: '3px solid white',
                        boxShadow: '0 3px 8px var(--color-overlay-35)',
                        cursor: 'grab',
                        zIndex: 1,
                      }}
                    />
                  </div>
                </Tooltip>
                <style>{`
                  @keyframes pulse-ring {
                    0% {
                      transform: scale(0.3);
                      opacity: 0.8;
                    }
                    100% {
                      transform: scale(1.5);
                      opacity: 0;
                    }
                  }
                `}</style>
              </Marker>
            ) : (
              userLocationData && (
                <Source
                  id="user-location-source"
                  type="geojson"
                  data={userLocationData}
                >
                  {/* @ts-ignore */}
                  <Layer {...userLocationLayer} />
                </Source>
              )
            )
          )}
        </>
      )}

      {showLegend && <MapPinLegend />}

      {onLocate && (
        <IconButton
          type="button"
          aria-label={mapLocateCopy.buttonAriaLabel}
          data-testid="map-locate-button"
          // Busy: a spinner, aria-busy, and presses ignored. aria-disabled (not `disabled`) so a
          // keyboard member's focus stays on the button.
          aria-busy={isLocating || undefined}
          aria-disabled={isLocating || undefined}
          icon={isLocating ? <Spinner size="sm" /> : <LocateFixed size={22} aria-hidden="true" />}
          onClick={isLocating ? undefined : onLocate}
          position="absolute"
          top={`${getLocateButtonTopPx(import.meta.env.DEV)}px`}
          right={`${LOCATE_BUTTON_INSET_PX}px`}
          zIndex={2}
          w={`${LOCATE_BUTTON_SIZE_PX}px`}
          minW={`${LOCATE_BUTTON_SIZE_PX}px`}
          h={`${LOCATE_BUTTON_SIZE_PX}px`}
          borderRadius="full"
          bg="var(--card-bg)"
          color="var(--card-text)"
          boxShadow="0 2px 8px var(--color-overlay-30)"
          _hover={{ bg: 'var(--card-bg)', opacity: 0.9 }}
          _focusVisible={FOCUS_VISIBLE_RING}
        />
      )}

      {/* Debug overlay - only visible in development */}
      {import.meta.env.DEV && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: 'var(--color-overlay-75)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontFamily: 'monospace',
            zIndex: 10,
            pointerEvents: 'none',
          }}
        >
          Zoom: {currentZoom.toFixed(2)}
        </div>
      )}
    </Map>
  );
});

RestaurantMapView.displayName = 'RestaurantMapView';

export default RestaurantMapView;
